import { Component, EventEmitter, Input, Output, signal, ChangeDetectionStrategy, ViewChild, ElementRef, input, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { LegendPosition } from "@swimlane/ngx-charts";
import { CurrencyPipe, DatePipe, DecimalPipe, NgClass } from "@angular/common";
import { MatTableModule } from "@angular/material/table";
import { MatSortModule } from "@angular/material/sort";
import { AbstractBuyerComponent } from "../abstract/buyer.component";
import { AuthService } from "../../service/auth.service";
import { AnalyticsService } from "../../service/analytics.service";
import { GeneralService } from "../../service/general.service";
import { Account } from "../../entity/Account";
import { User } from "../../entity/User";
import { PackageData } from "../../obj/PackageData";
import { OffersData } from "../../obj/OffersData";
import { SavingsInfo } from "../../obj/SavingsInfo";
import { DiffBenchmark } from "../../obj/DiffBenchmark";
import { Serie } from "../../obj/Serie";
import { RateData } from "../../obj/RateData";
import { ChartData } from "../../obj/ChartData";
import { KilowantBarHomeAcceptOfferChartComponent } from "../chart/kilowant-charts-home-accept-offer.component";
import { CarouselModule, OwlOptions, SlidesOutputData } from "ngx-owl-carousel-o";

/** One entry — the single source for both a slide and a table row. */
interface PackageSlide {
    chart: ChartData;
    offerIndex: number;
    kilowantPrice: number;

    rowPrice: number;
    rowStart: Date;
    rowEnd: Date;
    rowTerm: number | null;          // months for offers, null for gaps

    /** Resolved against the account's benchmark (utility or community). */
    rowSavings: SavingsInfo | null;
    /** Both benchmarks are kept separately so the tile can honour selectedDatasets. */
    rowSavingsUtility: SavingsInfo | null;
    rowSavingsCommunity: SavingsInfo | null;
}

@Component({
    selector: "kilowant-user-home-package-details",
    templateUrl: "../../../html/incs/user-home-package-details.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CarouselModule,
        KilowantBarHomeAcceptOfferChartComponent,
        CurrencyPipe,
        DatePipe,
        NgClass,
        MatTableModule,
        MatSortModule
    ]
})
export class UserHomePackageDetailsComponent extends AbstractBuyerComponent implements OnInit {

    @Input() client: User | null = null;
    account = input<Account | null>(null);
    @Input() packageData: PackageData | null = null;

    @Output("loaded") loaded = new EventEmitter<boolean>();
    @Output("closed") closed = new EventEmitter<boolean>();
    @Output("acceptPackage") acceptPackage = new EventEmitter<boolean>();

    @ViewChild("slider", { static: false }) slider: any;
    @ViewChild("innerBody") bodyElement: ElementRef;

    // ---- Chart state ----
    view = signal<[number, number]>([360, 300]);
    legend = signal<boolean>(true);
    legendTitle = signal<string>("");
    legendPosition = signal<LegendPosition>(LegendPosition.Below);
    xAxis = signal<boolean>(true);
    yAxis = signal<boolean>(true);
    showYAxisLabel = signal<boolean>(false);
    showXAxisLabel = signal<boolean>(false);
    xAxisLabel = signal<string>("Year");
    yAxisLabel = signal<string>("");
    colorScheme = signal<any>({
        domain: ["#34A853", "#A0A0A0"],
        name: "kilowant",
        selectable: true,
        group: null!
    });

    selectedDatasets = signal<number[]>([]);
    latestUtilityDate = signal<string | null>(null);
    latestCommunityDate = signal<string | null>(null);

    sliderData: ChartData[] = [];
    packageSlides: PackageSlide[] = [];

    positionOfFirstSellOfferSlide = 0;
    currentPosition = signal<number>(-1);
    initiated = false;

    sliderOptions = signal<OwlOptions>({
        loop: false,
        nav: true,
        items: 1,
        dots: false,
        navText: ["", ""],
        autoplay: false,
        smartSpeed: 1000,
        responsive: {
            0: { items: 1 },
            600: { items: 2 },
            1000: { items: 1 }
        }
    });

    /** First column shows the offer number ("№"). */
    displayedColumns: string[] = ["No", "Price", "ServiceStart", "ServiceEnd", "Term"];

    readonly DiffBenchmark = DiffBenchmark;

    constructor(
        router: Router,
        route: ActivatedRoute,
        dialog: MatDialog,
        protected datepipe: DatePipe,
        protected currencypipe: CurrencyPipe,
        protected numberpipe: DecimalPipe,
        authService: AuthService,
        analyticsService: AnalyticsService,
        generalService: GeneralService
    ) {
        super(router, route, dialog, authService, analyticsService, generalService);
    }

    ngOnInit(): void {
        this.start();
    }

    // ============================================================
    //  Single build pass — produces slides and table rows together
    // ============================================================

    start(): void {
        const pkg = this.packageData;
        if (pkg == null || pkg.offers == null || pkg.offers.length === 0) {
            return;
        }

        this.generalService.getAccountGetLatestUtilityDate(this.account()!.id).then(x => {
            this.latestUtilityDate.set(x.key != null ? this.datepipe.transform(x.key, "MMM. y") : null);
            this.latestCommunityDate.set(x.value != null ? this.datepipe.transform(x.value, "MMM. y") : null);
        });

        interface Pending {
            meta: { isGap: boolean; offerIndex: number; kilowantPrice: number };
            months: ChartData[];
        }
        const initPromises: Promise<Pending>[] = [];
        for (let i = 0; i < pkg.offers.length; i++) {
            const rec = pkg.offers[i];
            const offer = rec.offer;
            const offerPrice = offer.priceForBuyer();
            const offerEnd = this.getTermTo(offer.termFrom, offer.termPeriod);

            initPromises.push(
                this.generalService
                    .getContractRateListPerAccount(this.account()!, offer.termFrom, offerEnd)
                    .then(months => ({
                        meta: { isGap: false, offerIndex: i, kilowantPrice: offerPrice },
                        months: <ChartData[]>months
                    }))
            );
        }
        Promise.all(initPromises).then(results => {
            const slides: ChartData[] = [];
            const metas: PackageSlide[] = [];

            for (const entry of results) {
                if (entry.months == null) { continue; }
                const rec = pkg.offers[entry.meta.offerIndex];
                for (const chart of entry.months) {
                    slides.push(chart);
                    metas.push(this.makeSlideEntry(chart, entry.meta, rec));
                }
            }

            this.sliderData = slides;
            this.packageSlides = metas;

            this.view.update(v => [this.bodyElement.nativeElement.offsetWidth, v[1]]);

            this.positionOfFirstSellOfferSlide = 0;
            this.currentPosition.set(0);
            this.sliderOptions.update(opts => ({ ...opts, startPosition: 0 }));

            if (this.sliderData.length > 0) {
                this.changeDataset(this.account()!.isCommunity ? 2 : 1);
            }
            this.initiated = false;
            this.loaded.emit(true);
        });
    }

    private makeSlideEntry(
        chart: ChartData,
        meta: { isGap: boolean; offerIndex: number; kilowantPrice: number },
        owningOffer: OffersData
    ): PackageSlide {
        return {
            chart,
            offerIndex: meta.offerIndex,
            kilowantPrice: meta.kilowantPrice,
            rowPrice: owningOffer.offer.priceForBuyer(),
            rowStart: owningOffer.offer.termFrom,
            rowEnd: this.getTermTo(owningOffer.offer.termFrom, owningOffer.offer.termPeriod),
            rowTerm: owningOffer.offer.termPeriod,
            rowSavings: owningOffer.savings[this.account()!.isCommunity ? DiffBenchmark.COMMUNITY : DiffBenchmark.UTILITY]
                ?? owningOffer.savings[DiffBenchmark.UTILITY]
                ?? owningOffer.savings[DiffBenchmark.COMMUNITY]
                ?? null,
            rowSavingsUtility: owningOffer.savings[DiffBenchmark.UTILITY] ?? null,
            rowSavingsCommunity: owningOffer.savings[DiffBenchmark.COMMUNITY] ?? null
        };
    }

    private monthBounds(chart: ChartData): { start: Date; end: Date } {
        const arr = (chart.name ?? "").split("/");
        const m = parseInt(arr[0]);
        const y = parseInt(arr[1]);
        const start = (!isNaN(m) && !isNaN(y)) ? new Date(y, m - 1, 1) : new Date();
        const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
        return { start, end };
    }

    private utilityPriceOf(chart: ChartData): number {
        for (const rate of chart.rates ?? []) {
            for (const series of rate.series ?? []) {
                if (series.name !== "Kilowant") {
                    return series.value;
                }
            }
        }
        return 0;
    }

    // ============================================================
    //  Chart slider runtime
    // ============================================================

    sliderChange(data: SlidesOutputData): void {
        if (this.initiated) {
            const newPosition = Number(data.startPosition);
            if (newPosition !== this.currentPosition()) {
                this.currentPosition.set(newPosition);
            }
        } else {
            this.initiated = true;
        }
    }

    gotoSlide(i: number): void {
        if (i < 0 || i >= this.sliderData.length) { return; }
        if (this.slider != null) {
            this.slider.to(i.toString());
        } else {
            this.sliderOptions.update(opts => ({ ...opts, startPosition: i }));
            this.currentPosition.set(i);
        }
    }

    changeDataset(pos: number): void {
        if (this.selectedDatasets().includes(pos) && this.selectedDatasets().length > 1) {
            this.selectedDatasets.update(arr => arr.filter(item => item !== pos));
        } else if (!this.selectedDatasets().includes(pos)) {
            this.selectedDatasets.update(arr => [...arr, pos]);
        }
        for (let i = 0; i < this.sliderData.length; i++) {
            this.sliderData[i].activeRates = [];
            for (let x = 0; x < this.sliderData[i].rates.length; x++) {
                const r = new RateData(this.sliderData[i].rates[x].name, []);
                for (let y = 0; y < this.sliderData[i].rates[x].series.length; y++) {
                    if (this.selectedDatasets().includes((y + 1))) {
                        r.series.push(this.sliderData[i].rates[x].series[y]);
                    }
                }
                this.sliderData[i].activeRates.push(r);
            }
            const meta = this.packageSlides[i];
            if (meta != null && meta.kilowantPrice > 0) {
                this._addKilowantBar(i, meta.kilowantPrice);
            }
        }
    }

    _addKilowantBar(s: number, price: number): void {
        if (this.sliderData[s].activeRates != null) {
            for (let j = 0; j < this.sliderData[s].activeRates.length; j++) {
                this.sliderData[s].activeRates[j].series.push(
                    new Serie(
                        "Kilowant " + (new CurrencyPipe("en-US")).transform(price, "USD", "symbol", "1.3-3") + "/kWh",
                        price
                    )
                );
            }
        }
    }

    gotoPackage(): void {
        this.acceptPackage.emit(true);
    }

    // ============================================================
    //  Price statistics strip
    // ============================================================

    getStatsText(): string {
        const pkg = this.packageData;
        if (pkg == null || pkg.price == null || pkg.priceStats == null) { return ""; }
        const price = pkg.price;
        let min = pkg.priceStats.min;
        let max = pkg.priceStats.max;
        if (price < min) { min = price; }
        if (price > max) { max = price; }
        const diff = max - min;
        const lowBound = min + diff / 3;
        const highBound = max - diff / 3;
        if (price <= lowBound) { return "low"; }
        if (price >= highBound) { return "high"; }
        return "typical";
    }

    getStatsPercent(): number {
        const pkg = this.packageData;
        if (pkg == null || pkg.price == null || pkg.priceStats == null) { return 50; }
        const price = pkg.price;
        let min = pkg.priceStats.min;
        let max = pkg.priceStats.max;
        if (price < min) { min = price; }
        if (price > max) { max = price; }
        const diff = max - min;
        if (diff === 0) { return 50; }
        return Math.round((price - min) * 100 / diff);
    }

    // ============================================================
    //  Yellow tile — per-offer savings, filtered by selectedDatasets
    // ============================================================

    getCurrentSlide(): PackageSlide | null {
        const pos = this.currentPosition();
        if (pos < 0 || pos >= this.packageSlides.length) { return null; }
        return this.packageSlides[pos];
    }

    hasHeader(): boolean {
        return this.getCurrentSlide() != null;
    }

    /** True when the current slide is an offer (tile content is meaningful). */
    isCurrentOffer(): boolean {
        return this.getCurrentSlide() != null;
    }

    /** 1-based number of the offer the user is currently viewing (0 = none). */
    getCurrentOfferNumber(): number {
        const s = this.getCurrentSlide();
        if (s == null) { return 0; }
        return s.offerIndex + 1;
    }

    /** Total number of offers contained in this package. */
    getTotalOfferCount(): number {
        if (this.packageData == null || this.packageData.offers == null) { return 0; }
        return this.packageData.offers.length;
    }

    /** "Offer X of XXX" label for the tile; empty string when not applicable. */
    getOfferPositionText(): string {
        const current = this.getCurrentOfferNumber();
        const total = this.getTotalOfferCount();
        if (current <= 0 || total <= 0) { return ""; }
        return "Offer " + current + " of " + total;
    }

    /**
     * Utility-benchmark savings clause for the current slide.
     * Returns "" when there is no positive utility savings on the row.
     */
    private getUtilitySavingsClause(): string {
        const s = this.getCurrentSlide();
        if (s == null || s.rowSavingsUtility == null) { return ""; }

        const sv = s.rowSavingsUtility;
        if (sv.diffVsBenchmark == null || sv.diffVsBenchmark <= 0) { return ""; }

        const label = "vs. RI Energy";
        if (sv.diffVsBenchmarkInUSD != null && sv.diffVsBenchmarkInUSD > 0) {
            return "Save $" + this.numberpipe.transform(sv.diffVsBenchmarkInUSD, "", "en-US")
                + " (" + sv.diffVsBenchmark + "%) " + label;
        }
        return "Save " + sv.diffVsBenchmark + "% " + label;
    }

    /**
     * Community-benchmark savings clause for the current slide.
     * Returns "" when there is no positive community savings on the row.
     */
    private getCommunitySavingsClause(): string {
        const s = this.getCurrentSlide();
        if (s == null || s.rowSavingsCommunity == null) { return ""; }

        const sv = s.rowSavingsCommunity;
        if (sv.diffVsBenchmark == null || sv.diffVsBenchmark <= 0) { return ""; }

        const label = "vs. NextEra Energy";
        if (sv.diffVsBenchmarkInUSD != null && sv.diffVsBenchmarkInUSD > 0) {
            return "Save $" + this.numberpipe.transform(sv.diffVsBenchmarkInUSD, "", "en-US")
                + " (" + sv.diffVsBenchmark + "%) " + label;
        }
        return "Save " + sv.diffVsBenchmark + "% " + label;
    }

    /**
     * Final tile text, built from the current slide's savings and the
     * selectedDatasets signal (1 = utility, 2 = community).
     *
     *   * Only 1 selected and utility savings > 0 -> utility clause.
     *   * Only 2 selected and community savings > 0 -> community clause.
     *   * 1 and 2 selected -> whichever clauses have positive savings, joined
     *     by " and ". If neither is positive, "" is returned.
     */
    getOfferSavingsMainText(): string {
        const datasets = this.selectedDatasets();
        const utilitySelected = datasets.includes(1);
        const communitySelected = datasets.includes(2);

        const utilityClause = utilitySelected ? this.getUtilitySavingsClause() : "";
        const communityClause = communitySelected ? this.getCommunitySavingsClause() : "";

        if (utilityClause !== "" && communityClause !== "") {
            return utilityClause + " and " + communityClause;
        }
        if (utilityClause !== "") { return utilityClause; }
        if (communityClause !== "") { return communityClause; }
        return "";
    }

    /** True when a tile text is available for the current slide. */
    hasOfferSavingsText(): boolean {
        return this.getOfferSavingsMainText() !== "";
    }

    /** Background colour for the tile. A package is always a Kilowant artefact. */
    getHeaderTileBackground(): string {
        return this.getKilowantColor();
    }

    // ============================================================
    //  Table helpers
    // ============================================================

    getTermText(slide: PackageSlide): string {
        if (slide.rowTerm == null) { return "-"; }
        return slide.rowTerm + " mths";
    }

    getSlideDatesText(slide: PackageSlide): string {
        return this.datepipe.transform(slide.rowStart, "M/yy")
            + " - "
            + this.datepipe.transform(slide.rowEnd, "M/yy");
    }

    getHeaderTermText(): string {
        const s = this.getCurrentSlide();
        if (s == null) { return ""; }
        return this.datepipe.transform(s.rowStart, "M/yy")
            + " - "
            + this.datepipe.transform(s.rowEnd, "M/yy")
            + ", " + s.rowTerm + " mths";
    }

}